import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { PaginationState } from "@tanstack/react-table";

export type TableControl = {
  pagination: PaginationState;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
  search: string | null;
  setSearch: Dispatch<SetStateAction<string | null>>;
};

export function useTableControl(props?: {
  initialPageSize?: number;
  /**
   * Page number and posts per page should be persisted in the URL
   * like this: /sessions?page=1&size=10
   */
  persistInUrl?: boolean;
}): TableControl {
  const { persistInUrl = true } = props ?? {};
  const [searchParams, setSearchParams] = useSearchParams();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: Number(searchParams.get("page")) || 0,
    pageSize: Number(searchParams.get("size")) || props?.initialPageSize || 10,
  });

  const [search, setSearch] = useState<string | null>(
    searchParams.get("search") || null,
  );

  useEffect(() => {
    if (!persistInUrl) return;
    setSearchParams((prev) => {
      prev.set("page", pagination.pageIndex.toString());
      prev.set("size", pagination.pageSize.toString());
      return prev;
    });
  }, [pagination, setSearchParams, persistInUrl]);

  useEffect(() => {
    if (!persistInUrl) return;
    setSearchParams((prev) => {
      if (search) {
        prev.set("search", search);
      } else {
        prev.delete("search");
      }
      return prev;
    });
  }, [search, setSearchParams, persistInUrl]);

  return { pagination, setPagination, search, setSearch };
}
